import LRUCache from './LRUCache';
import { SanitizerReturnType } from './sanitizerReturnType';
import type { OwaSanitizerConfig } from './owaSanitizerConfig';
import { shouldUseNativeSanitizer } from './shouldUseNativeSanitizer';
import { sanitizeWithDomPurify } from './domPurifySanitizer';
import { lazySanitizeWithNativeAPI } from '../lazyFunctions';
import { removeStyleComments } from './removeStyleComments';
import type { sanitizeWithNativeAPI as SanitizeWithNativeAPIType } from './nativeSanitizer';

let _sanitizeWithNativeAPI: typeof SanitizeWithNativeAPIType | undefined;

/**
 * This cache is used to store sanitized HTML strings to avoid re-sanitizing the same HTML multiple times.
 * The number can increase if we see more cases of HTML sanitizing.
 */
let sanitizedStringCache: LRUCache<string> | undefined;

/**
 * This cache is used to store html strings with style comments removed to avoid re-processing the same HTML multiple times.
 */
let styleCommentsCache: LRUCache<string> | undefined;

// Type mapping for return types
type ReturnTypeMap = {
    [SanitizerReturnType.String]: string;
    [SanitizerReturnType.TrustedHTML]: string;
    [SanitizerReturnType.HTMLElement]: HTMLElement;
    [SanitizerReturnType.DocumentFragment]: DocumentFragment;
    [SanitizerReturnType.Node]: Node;
};

// Function overloads using generics
function getTrustedHTML<T extends SanitizerReturnType>(
    htmlString: string,
    returnType: T
): ReturnTypeMap[T];
function getTrustedHTML<T extends SanitizerReturnType>(
    htmlString: string,
    returnType: T,
    extendedConfig: OwaSanitizerConfig,
    optionalCacheID?: string
): ReturnTypeMap[T];

function getTrustedHTML<T extends SanitizerReturnType>(
    htmlString: string,
    returnType: T,
    extendedConfig: OwaSanitizerConfig = {},
    optionalCacheID?: string
): ReturnTypeMap[T] {
    if (!htmlString) {
        // If htmlString is not defined, default to empty string to avoid DOMPurify returning null
        htmlString = '';
    }

    // Create a unique cache key based on the HTML string and the serialized configuration
    const cacheKey = `${optionalCacheID ?? htmlString}:${JSON.stringify(
        extendedConfig
    )}:${JSON.stringify(returnType)}`;

    // Define one single instance of cache if is undefined
    if (sanitizedStringCache === undefined) {
        sanitizedStringCache = new LRUCache<string>(150);
    }

    // Define one single instance of styleCommentsCache if is undefined
    if (styleCommentsCache === undefined) {
        styleCommentsCache = new LRUCache<string>(150);
    }

    const cachedStyleComments = styleCommentsCache.get(cacheKey);

    if (cachedStyleComments) {
        htmlString = cachedStyleComments;
    } else {
        // Remove style-tag comments before sanitizing and cache the value
        const processed = removeStyleComments(htmlString);
        styleCommentsCache.set(cacheKey, processed);
        htmlString = processed;
    }

    // --- Dual-path dispatch: native Sanitizer API vs DOMPurify ---

    const useNative = shouldUseNativeSanitizer();

    if (useNative) {
        if (_sanitizeWithNativeAPI) {
            if (
                returnType === SanitizerReturnType.String ||
                returnType === SanitizerReturnType.TrustedHTML
            ) {
                const sanitizedStringValue = sanitizedStringCache.get(cacheKey);
                if (sanitizedStringValue) {
                    return sanitizedStringValue as ReturnTypeMap[T];
                }

                const result = _sanitizeWithNativeAPI(
                    htmlString,
                    returnType,
                    extendedConfig
                ) as string;

                if (htmlString) {
                    sanitizedStringCache.set(cacheKey, result);
                }

                return result as ReturnTypeMap[T];
            }

            return _sanitizeWithNativeAPI(
                htmlString,
                returnType,
                extendedConfig
            ) as ReturnTypeMap[T];
        }

        // Module not loaded yet — kick off load and store the function for subsequent calls.
        // LazyImport deduplicates internally so this is safe to call on every miss.
        lazySanitizeWithNativeAPI.import().then(sanitizeWithNativeAPI => {
            _sanitizeWithNativeAPI = sanitizeWithNativeAPI;
        });
    }

    // --- DOMPurify path (existing behavior) ---

    if (
        returnType === SanitizerReturnType.String ||
        returnType === SanitizerReturnType.TrustedHTML
    ) {
        // Check if the sanitized html string is in the cache
        const sanitizedStringValue = sanitizedStringCache.get(cacheKey);

        if (sanitizedStringValue) {
            return sanitizedStringValue as ReturnTypeMap[T];
        }

        const trustedHTML = sanitizeWithDomPurify(htmlString, returnType, extendedConfig) as string;

        // Only cache when NOT expecting native, this is a temporary DOMPurify fallback
        // when useNative is true and _sanitizeWithNativeAPI is not yet available.
        if (htmlString && !useNative) {
            sanitizedStringCache.set(cacheKey, trustedHTML);
        }

        return trustedHTML as ReturnTypeMap[T];
    }

    return sanitizeWithDomPurify(htmlString, returnType, extendedConfig) as ReturnTypeMap[T];
}

export { getTrustedHTML, SanitizerReturnType };
